/**
 * The ONE reader and range for a schedule block's stored date, shared by the booking form, the Edit Schedule form, the "needs a person"
 * bar and the daily stand-in reminder (SCHED-YEAR-1). Before this the range was written in three places and the readers disagreed
 * about a stored "10/26/2026 09:00:00".
 *
 * A stored date is "MM/DD/YYYY" (AppSheet's format), optionally followed by a time, or ISO "YYYY-MM-DD", optionally followed by "T" or a
 * space and a time. The year is exactly four digits and must be in range; the month and day must exist. It never reads a year 0-99
 * as 1900-1999 (`new Date(3, 9, 26)` is 1903), and it never rolls an impossible day into the next month.
 * NAMED LIMIT: only the DATE is validated. Any time that follows it ("10/26/2026 25:99", "2026-10-26Tgarbage") is ignored here, not
 * checked; the time is a separate field with its own editor.
 *
 * The SERVER's own refusal of an out-of-range date on create/update is a separate change (PR #8377, `parseBlockDate`); it uses the same range.
 */
// NOTE (intended difference): this reader accepts the shapes STORED on a block (US "M/D/YYYY" with an optional time tail, padded
// ISO). The server's parseBlockDate (#8377) accepts the shapes SUBMITTED to it (unpadded ISO, local time tail). Do not unify them.
export const SCHEDULE_BLOCK_MIN_YEAR = 2000;
export const SCHEDULE_BLOCK_MAX_YEAR = 2100;

export interface StoredBlockDateParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/** The parts of a stored block date, or null when it is not a real date with a four-digit year in range. */
export function parseStoredBlockDateParts(raw: string | null | undefined): StoredBlockDateParts | null {
  const value = (raw ?? '').trim();
  if (value === '') return null;
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s.*)?$/.exec(value);
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(value);
  const [month, day, year] = us
    ? [Number(us[1]), Number(us[2]), Number(us[3])]
    : iso
      ? [Number(iso[2]), Number(iso[3]), Number(iso[1])]
      : [NaN, NaN, NaN];
  if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(year)) return null;
  if (year < SCHEDULE_BLOCK_MIN_YEAR || year > SCHEDULE_BLOCK_MAX_YEAR || month < 1 || month > 12 || day < 1) return null;
  const real = new Date(Date.UTC(year, month - 1, day));
  const isReal = real.getUTCFullYear() === year && real.getUTCMonth() === month - 1 && real.getUTCDate() === day;
  return isReal ? { year, month, day } : null;
}

/** True when a stored block date is a real date in the booking range (the same answer everywhere). */
export function isStoredBlockDateValid(raw: string | null | undefined): boolean {
  return parseStoredBlockDateParts(raw) !== null;
}
